// client/src/components/pages/LexiStar/Commissioning/overrideRequest.ts
//
// The floor override of a parallel-run sign-off, as the commissioning pages show it
// (SR-21, ST-00193). The override always takes two users, each in their own session:
//   1. one asks for it with a reason  — POST /parallel-runs/:id:signOff {override_reason} → 202
//   2. a DIFFERENT authorised user signs off THAT request — the same call naming it,
//      {confirm_override_id: pending_override.id}; an empty body never confirms, and a request
//      replaced since is refused (409 override_changed with the current one).
// The server never takes the second user's name in the body (second_actor → 400). A request
// expires (default 24 h, LEXISTAR_OVERRIDE_PENDING_HOURS); a re-run of the comparison drops it.
// The server marks each request for the viewer (requested_by_you, can_confirm, expired); the
// helpers below fall back to the session user and the clock when a field is missing.

import { isMe as isMeJs } from '../ReviewQueue/assuranceShared';

/** report.pending_override as GET /parallel-runs(/:id) returns it for the caller. */
export interface PendingOverride {
  /** What the second user's sign-off names (confirm_override_id). */
  id?: string;
  requested_by: string;
  requested_by_user_id?: number | string | null;
  reason: string;
  requested_at: string;
  expires_at?: string | null;
  failing?: string[];
  report_computed_at?: string | null;
  expired?: boolean;
  requested_by_you?: boolean;
  can_confirm?: boolean;
}

/** The session user, as AuthContext gives it. */
export interface OverrideViewer {
  userId?: number | string | null;
  email?: string | null;
  username?: string | null;
}

/**
 *   none     no request: ask for one with a reason
 *   mine     the viewer asked; a different authorised user must sign it off
 *   theirs   someone else asked; the viewer can sign it off as the second user
 *   expired  nobody signed it off in time; ask again
 */
export type OverrideRequestStatus = 'none' | 'mine' | 'theirs' | 'expired';

const isMe = isMeJs as unknown as (user: OverrideViewer | null | undefined, actor: unknown) => boolean;

/** Is the request past its expiry (the server's flag first, else expires_at against `now`)? */
export function isExpired(p: PendingOverride, now: number = Date.now()): boolean {
  if (typeof p.expired === 'boolean') return p.expired;
  const at = p.expires_at ? Date.parse(p.expires_at) : NaN;
  return Number.isFinite(at) ? now >= at : false;
}

/** Did the viewer ask for it (the server's flag first, else the session user's identities)? */
export function isRequestedByViewer(
  p: PendingOverride,
  user: OverrideViewer | null | undefined
): boolean {
  if (typeof p.requested_by_you === 'boolean') return p.requested_by_you;
  if (
    user?.userId != null &&
    p.requested_by_user_id != null &&
    String(user.userId) === String(p.requested_by_user_id)
  )
    return true;
  return isMe(user, p.requested_by);
}

export function overrideRequestStatus(
  p: PendingOverride | null | undefined,
  user: OverrideViewer | null | undefined,
  now: number = Date.now()
): OverrideRequestStatus {
  if (!p || !p.requested_by) return 'none';
  if (isExpired(p, now)) return 'expired';
  return isRequestedByViewer(p, user) ? 'mine' : 'theirs';
}

/** A parallel run as the list returns it (only what the pending list reads). */
export interface RunWithOverride {
  id: string;
  name?: string | null;
  state?: string | null;
  certificate_id?: string | null;
  pending_override?: PendingOverride | null;
}

/** Runs with a live override request, oldest request first (the "awaiting a second user" list). */
export function runsAwaitingSecondUser<R extends RunWithOverride>(
  runs: readonly R[],
  user: OverrideViewer | null | undefined,
  now: number = Date.now()
): Array<R & { pending_override: PendingOverride; status: 'mine' | 'theirs' }> {
  const out: Array<R & { pending_override: PendingOverride; status: 'mine' | 'theirs' }> = [];
  for (const r of runs) {
    if (r.state === 'signed_off' || r.certificate_id) continue;
    const p = r.pending_override;
    const status = overrideRequestStatus(p, user, now);
    if (p && (status === 'mine' || status === 'theirs')) out.push({ ...r, pending_override: p, status });
  }
  return out.sort((a, b) =>
    String(a.pending_override.requested_at).localeCompare(String(b.pending_override.requested_at))
  );
}
